import React, { useState, useEffect } from "react";
import {
  Upload,
  File,
  CheckCircle,
  X,
  Loader,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import useAuth from "../contexts/AuthContext";
import { IMAGE_CERT } from "../constants";

interface DocumentUploadProps {
  applicationId: string;
  docType: string;
  label: string;
  required?: boolean;
  existingDoc?: { key: string; url?: string; label?: string } | null;
  onUploadComplete?: (docType: string, docData: { key: string; url: string; label?: string } | null) => void;
  folder?: string;
  disabled?: boolean;
}

export default function DocumentUpload({
  applicationId,
  docType,
  label,
  required = false,
  existingDoc = null,
  onUploadComplete,
  folder,
  disabled = false,
}: DocumentUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [document, setDocument] = useState<{ key: string; url?: string; label?: string } | null>(existingDoc);
  const { apiRequest } = useAuth();

  // Update document state when existingDoc changes
  useEffect(() => {
    setDocument(existingDoc);
  }, [existingDoc]);

  const getFileExtension = (filename: string) => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  const validateFile = (file: File) => {
    const maxSize = IMAGE_CERT.includes(docType)
      ? 1 * 1024 * 1024 // 1 mb for image
      : 10 * 1024 * 1024; // 10MB for pdf

    // Determine allowed extensions based on docType
    const allowedTypes = IMAGE_CERT.includes(docType)
      ? ["jpg", "jpeg", "png"]
      : ["pdf", "jpg", "jpeg", "png"];

    const extension = getFileExtension(file.name).toLowerCase(); // normalize to lowercase

    if (file.size > maxSize) {
      return "File size must be less than 5MB";
    }

    if (!allowedTypes.includes(extension)) {
      // Dynamic error message based on allowedTypes
      return `Only ${allowedTypes
        .map((ext) => ext.toUpperCase())
        .join(", ")} files are allowed`;
    }

    return null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    await uploadFile(file);

    // Reset input value to allow re-uploading the same file
    e.target.value = "";
  };

  const uploadFile = async (file: File) => {
    if (!applicationId) {
      setError(
        "Application ID is required. Please save your application first."
      );
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      // Step 1: Get presigned upload URL
      const fileExtension = getFileExtension(file.name);
      setUploadProgress(20);

      const data = await apiRequest(
        `/uploads/generate-upload-url/${applicationId}/${docType}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileExtension,
            contentType: file.type,
            folder,
            oldKey: document?.key, // Send old key for deletion
          }),
        }
      );

      const { uploadUrl, key, contentType: signedContentType } = data;
      setUploadProgress(40);

      // Step 2: Upload file to S3
      const uploadContentType = signedContentType || file.type || "application/octet-stream";
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": uploadContentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3. Please try again.");
      }

      setUploadProgress(75);

      // Step 3: Confirm upload with backend
      const confirmRes = await apiRequest(
        `/uploads/confirm-upload/${applicationId}/${docType}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            key,
            oldKey: document?.key, // Send old key for deletion
          }),
        }
      );

      const { url } = confirmRes;
      setUploadProgress(100);

      const newDocData = { key, url, label };
      setDocument(newDocData);

      if (onUploadComplete) {
        onUploadComplete(docType, newDocData);
      }

      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Upload failed. Please try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!document?.key) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this document?"
    );
    if (!confirmed) return;

    try {
      setUploading(true);

      await apiRequest(
        `/uploads/delete-upload/${encodeURIComponent(document.key)}`,
        {
          method: "DELETE",
        }
      );

      setDocument(null);
      if (onUploadComplete) {
        onUploadComplete(docType, null);
      }
    } catch (err) {
      setError("Failed to delete document. Please try again.");
      console.error("Delete error:", err);
    } finally {
      setUploading(false);
    }
  };

  const openDocument = async () => {
    if (!document?.key) return;

    try {
      // Generate a fresh presigned URL for viewing
      const response = await apiRequest(
        `/uploads/view-url/${encodeURIComponent(document.key)}`,
        {
          method: "GET",
        }
      );

      if (response.url) {
        window.open(response.url, "_blank");
      }
    } catch (err) {
      // Fallback to stored URL if available
      if (document?.url) {
        window.open(document.url, "_blank");
      } else {
        setError("Unable to open document. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {!document?.key ? (
        <div className="relative">
          <input
            type="file"
            onChange={handleFileSelect}
            disabled={uploading || disabled}
            className="hidden"
            id={`file-${docType}`}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <label
            htmlFor={`file-${docType}`}
            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
              uploading || disabled
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center mb-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <p className="text-sm text-gray-600">
                  Uploading... {uploadProgress}%
                </p>
                <div className="w-48 h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    className="h-full bg-blue-600"
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, JPEG, PNG {IMAGE_CERT.includes(docType) ? "" : ", PDF"}{" "}
                  (Max {IMAGE_CERT.includes(docType) ? "1" : "10"}MB)
                </p>
              </>
            )}
          </label>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                Document uploaded successfully
              </p>
              <p className="text-xs text-gray-500">Click to view document</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openDocument}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
              title="View document"
              disabled={uploading}
            >
              <File className="w-5 h-5" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
              title="Delete document"
              disabled={uploading || disabled}
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              ) : (
                <X className="w-5 h-5" />
              )}
            </button>
          </div>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </motion.div>
      )}

      <p className="text-xs text-gray-500">
        Uploaded documents are securely stored and accessible only to you and
        authorized personnel.
      </p>
    </div>
  );
}
